import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';
import { TableSkeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Pagination as PaginationControl } from '../components/ui/Pagination';
import { Plus, Edit2, Trash2, Search, X } from 'lucide-react';

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface AsramaRow {
  id: number;
  nama: string;
  jenis: 'putra' | 'putri';
  is_active: boolean;
  total_kamar: number;
  total_penghuni: number;
}

interface KamarRow {
  id: number;
  asrama_id: number;
  nama: string;
  terisi: number;
}

interface AsramaSimple {
  id: number;
  nama: string;
}

// Kamar di dalam form asrama. `id` kosong = kamar baru yang belum disimpan.
interface KamarDraft {
  id?: number;
  nama: string;
  terisi: number;
}

// Backend masih mewajibkan kapasitas kamar, jenis, dan status aktif asrama.
// Form tidak menampilkannya: kamar baru diberi kapasitas besar (tidak pernah
// penuh), asrama baru diberi jenis 'putra' dan aktif, sedangkan asrama yang
// diubah mempertahankan nilai lamanya.
const KAPASITAS_DEFAULT = 50;

const pad2 = (n: number) => String(n).padStart(2, '0');

export function AsramaPage() {
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const queryClient = useQueryClient();

  const canCreate = hasPermission('kelas', 'tambah');
  const canEdit = hasPermission('kelas', 'ubah');
  const canDelete = hasPermission('kelas', 'hapus');

  // Filter & pagination
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AsramaRow | null>(null);
  const [nama, setNama] = useState('');
  const [kamarList, setKamarList] = useState<KamarDraft[]>([]);
  const [originalKamar, setOriginalKamar] = useState<KamarDraft[]>([]);
  const [loadingKamar, setLoadingKamar] = useState(false);
  const [kamarInput, setKamarInput] = useState('');
  const [gen, setGen] = useState({ jumlah: 10, awalan: 'Kamar', mulai: 1 });

  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['asrama', search, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search.trim()) params.set('search', search.trim());
      const res = await api.get<{ items: AsramaRow[]; pagination: Pagination }>(
        `/api/asrama?${params.toString()}`
      );
      return res.data;
    },
  });

  const list = data?.items || [];

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['asrama'] });
    queryClient.invalidateQueries({ queryKey: ['asrama-simple'] });
    queryClient.invalidateQueries({ queryKey: ['kamar'] });
    queryClient.invalidateQueries({ queryKey: ['kamar-simple'] });
  };

  const fetchKamarAsrama = async (asramaId: number): Promise<KamarDraft[]> => {
    const res = await api.get<{ items: KamarRow[] }>(
      `/api/kamar?asrama_id=${asramaId}&page=1&limit=100`
    );
    const rows = res.data?.items || [];
    return rows
      .map((k) => ({ id: k.id, nama: k.nama, terisi: k.terisi }))
      .sort((a, b) => a.nama.localeCompare(b.nama, undefined, { numeric: true }));
  };

  // ---------- Daftar kamar di dalam modal ----------
  const addKamarNames = (names: string[]) => {
    const bersih = names.map((n) => n.trim()).filter(Boolean);
    if (bersih.length === 0) return;
    setKamarList((prev) => {
      const seen = new Set(prev.map((k) => k.nama.toLowerCase()));
      const next = [...prev];
      for (const n of bersih) {
        const key = n.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          next.push({ nama: n, terisi: 0 });
        }
      }
      return next;
    });
  };

  const handleAddKamarInput = () => {
    addKamarNames(kamarInput.split(','));
    setKamarInput('');
  };

  const handleGenerate = () => {
    const awalan = gen.awalan.trim();
    if (!awalan) {
      error('Awalan nama kamar wajib diisi.');
      return;
    }
    const jumlah = Math.min(100, Math.max(1, Number(gen.jumlah) || 1));
    const mulai = Math.max(1, Number(gen.mulai) || 1);
    addKamarNames(Array.from({ length: jumlah }, (_, i) => `${awalan} ${pad2(mulai + i)}`));
  };

  const removeKamar = (index: number) => {
    setKamarList((prev) => prev.filter((_, i) => i !== index));
  };

  // ---------- Simpan asrama + kamar ----------
  const saveMutation = useMutation({
    mutationFn: async (kamar: KamarDraft[]) => {
      const payload = {
        nama: nama.trim(),
        jenis: editingItem?.jenis ?? 'putra',
        is_active: editingItem?.is_active ?? true,
      };

      // 1. Simpan asrama
      let asramaId: number;
      if (editingItem) {
        await api.put(`/api/asrama/${editingItem.id}`, payload);
        asramaId = editingItem.id;
      } else {
        const res: any = await api.post('/api/asrama', payload);
        asramaId = Number(res?.data?.id ?? res?.data?.data?.id);
        if (!asramaId) {
          // Cadangan: cari asrama yang baru dibuat lewat nama
          const simple = await api.get<AsramaSimple[]>('/api/asrama/simple');
          const found = (simple.data || [])
            .filter((a) => a.nama.toLowerCase() === payload.nama.toLowerCase())
            .sort((a, b) => b.id - a.id)[0];
          if (!found) throw new Error('Asrama tersimpan, tetapi ID-nya tidak ditemukan.');
          asramaId = found.id;
        }
      }

      // 2. Sinkronkan kamar: hapus yang dibuang, tambah yang baru
      const keptIds = new Set(kamar.filter((k) => k.id).map((k) => k.id));
      const toDelete = originalKamar.filter((k) => k.id && !keptIds.has(k.id));
      const toCreate = kamar.filter((k) => !k.id);

      const gagal: string[] = [];
      const jalankan = async (label: string, fn: () => Promise<unknown>) => {
        try {
          await fn();
        } catch (err: any) {
          gagal.push(`${label}: ${err?.message || 'gagal'}`);
        }
      };

      for (const k of toDelete) {
        await jalankan(k.nama, () => api.delete(`/api/kamar/${k.id}`));
      }
      for (const k of toCreate) {
        await jalankan(k.nama, () =>
          api.post('/api/kamar', {
            asrama_id: asramaId,
            nama: k.nama.trim(),
            kapasitas: KAPASITAS_DEFAULT,
            is_active: true,
          })
        );
      }

      return { gagal };
    },
    onSuccess: ({ gagal }) => {
      refresh();
      setModalOpen(false);
      if (gagal.length > 0) {
        error(
          `Asrama tersimpan, tetapi ${gagal.length} kamar gagal diproses: ${gagal
            .slice(0, 3)
            .join('; ')}${gagal.length > 3 ? ' ...' : ''}`
        );
      } else {
        success(editingItem ? 'Asrama berhasil diperbarui.' : 'Asrama berhasil ditambahkan.');
      }
    },
    onError: (err: any) => error(err.message || 'Gagal menyimpan asrama.'),
  });

  // Hapus asrama: kamarnya dihapus dulu (backend menolak asrama yang masih punya kamar)
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const kamar = await fetchKamarAsrama(id);
      for (const k of kamar) {
        if (k.id) await api.delete(`/api/kamar/${k.id}`);
      }
      await api.delete(`/api/asrama/${id}`);
    },
    onSuccess: () => {
      refresh();
      success('Asrama berhasil dihapus.');
      setDeleteId(null);
    },
    onError: (err: any) => {
      refresh();
      error(err.message || 'Gagal menghapus asrama.');
      setDeleteId(null);
    },
  });

  const handleOpenModal = async (item?: AsramaRow) => {
    setKamarInput('');
    setGen({ jumlah: 10, awalan: 'Kamar', mulai: 1 });
    setKamarList([]);
    setOriginalKamar([]);

    if (item) {
      setEditingItem(item);
      setNama(item.nama);
      setModalOpen(true);
      setLoadingKamar(true);
      try {
        const kamar = await fetchKamarAsrama(item.id);
        setKamarList(kamar);
        setOriginalKamar(kamar.map((k) => ({ ...k })));
      } catch (err: any) {
        error(err.message || 'Gagal memuat daftar kamar.');
      } finally {
        setLoadingKamar(false);
      }
    } else {
      setEditingItem(null);
      setNama('');
      setModalOpen(true);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (nama.trim().length < 3) {
      error('Nama asrama minimal 3 karakter.');
      return;
    }

    // Masukkan juga kamar yang masih diketik tapi belum ditekan Tambah
    const merged = [...kamarList];
    const seen = new Set(merged.map((k) => k.nama.trim().toLowerCase()));
    for (const n of kamarInput.split(',').map((s) => s.trim()).filter(Boolean)) {
      if (!seen.has(n.toLowerCase())) {
        seen.add(n.toLowerCase());
        merged.push({ nama: n, terisi: 0 });
      }
    }

    saveMutation.mutate(merged);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Asrama & Kamar</h2>
          <p className="text-xs text-slate-500 mt-1">
            Daftar asrama beserta kamarnya. Dipilih di form Data Siswa.
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-semibold shadow-xs transition-colors cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            Tambah Asrama
          </button>
        )}
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Cari nama asrama..."
          className="w-full pl-10 pr-3.5 py-2.5 text-xs border border-slate-200 rounded-xl bg-white focus:outline-emerald-600"
        />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
        {isLoading ? (
          <div className="p-6">
            <TableSkeleton rows={4} cols={4} />
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            title="Belum Ada Asrama"
            description="Tambahkan asrama beserta daftar kamarnya."
            onAction={canCreate ? () => handleOpenModal() : undefined}
            actionText="Tambah Asrama"
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200/80 text-slate-600 font-semibold uppercase tracking-wider text-[11px]">
                  <tr>
                    <th className="py-3.5 px-4">Nama Asrama</th>
                    <th className="py-3.5 px-4 text-center">Jumlah Kamar</th>
                    <th className="py-3.5 px-4 text-center">Penghuni</th>
                    <th className="py-3.5 px-4 text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {list.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50/70 transition-colors">
                      <td className="py-3.5 px-4 font-semibold text-slate-900">{item.nama}</td>
                      <td className="py-3.5 px-4 font-semibold text-center">{item.total_kamar}</td>
                      <td className="py-3.5 px-4 font-semibold text-center">{item.total_penghuni}</td>
                      <td className="py-3.5 px-4 text-center">
                        <div className="flex items-center justify-center gap-0 sm:gap-1.5">
                          {canEdit && (
                            <button
                              onClick={() => handleOpenModal(item)}
                              className="p-1.5 text-slate-500 hover:text-emerald-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                              title="Ubah asrama & kamar"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => setDeleteId(item.id)}
                              className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                              title="Hapus"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data?.pagination && (
              <PaginationControl
                currentPage={data.pagination.page}
                totalPages={data.pagination.totalPages}
                totalItems={data.pagination.total}
                itemsPerPage={data.pagination.limit}
                onPageChange={(p) => setPage(p)}
                onLimitChange={(l) => {
                  setLimit(l);
                  setPage(1);
                }}
              />
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingItem ? 'Ubah Asrama' : 'Tambah Asrama'}
        maxWidth="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Nama Asrama</label>
            <input
              type="text"
              value={nama}
              onChange={(e) => setNama(e.target.value)}
              placeholder="Contoh: Sunan Ampel"
              className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:outline-emerald-600"
              required
            />
          </div>

          {/* Daftar Kamar */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Daftar Kamar</label>

            {loadingKamar ? (
              <p className="text-xs text-slate-400 mb-2">Memuat daftar kamar...</p>
            ) : (
              kamarList.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2 max-h-40 overflow-y-auto">
                  {kamarList.map((k, i) => (
                    <span
                      key={k.id ?? `baru-${k.nama}`}
                      className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold"
                    >
                      {k.nama}
                      {k.terisi > 0 && (
                        <span className="text-[10px] font-medium text-emerald-700">
                          ({k.terisi})
                        </span>
                      )}
                      <button
                        type="button"
                        disabled={k.terisi > 0}
                        onClick={() => removeKamar(i)}
                        className="p-0.5 rounded hover:bg-slate-200 text-slate-500 disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer disabled:cursor-not-allowed"
                        title={
                          k.terisi > 0
                            ? `Kamar dihuni ${k.terisi} siswa, pindahkan dulu`
                            : 'Hapus kamar'
                        }
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )
            )}

            <div className="flex gap-2">
              <input
                type="text"
                value={kamarInput}
                onChange={(e) => setKamarInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    handleAddKamarInput();
                  }
                }}
                placeholder="Ketik nama kamar lalu Enter, contoh: Kamar 01"
                className="flex-1 px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:outline-emerald-600"
              />
              <button
                type="button"
                onClick={handleAddKamarInput}
                className="px-3.5 py-2 text-xs font-semibold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded-xl cursor-pointer"
              >
                Tambah
              </button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Semua kamar satu asrama dimasukkan di sini. Angka hijau di chip = jumlah penghuni.
            </p>

            {/* Buat banyak sekaligus */}
            <div className="mt-3 flex flex-wrap items-end gap-2 p-3 rounded-xl bg-slate-50 border border-slate-200">
              <div>
                <label className="block text-[10px] text-slate-500 mb-0.5">Awalan</label>
                <input
                  type="text"
                  value={gen.awalan}
                  onChange={(e) => setGen({ ...gen, awalan: e.target.value })}
                  className="w-28 px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-emerald-600 bg-white"
                />
              </div>
              <div>
                <label className="block text-[10px] text-slate-500 mb-0.5">Mulai no.</label>
                <input
                  type="number"
                  min={1}
                  value={gen.mulai}
                  onChange={(e) => setGen({ ...gen, mulai: Number(e.target.value) })}
                  className="w-20 px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-emerald-600 bg-white"
                />
              </div>
              <div>
                <label className="block text-[10px] text-slate-500 mb-0.5">Jumlah</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={gen.jumlah}
                  onChange={(e) => setGen({ ...gen, jumlah: Number(e.target.value) })}
                  className="w-20 px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-emerald-600 bg-white"
                />
              </div>
              <button
                type="button"
                onClick={handleGenerate}
                className="px-3.5 py-1.5 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 rounded-lg cursor-pointer"
              >
                Buat banyak kamar
              </button>
              <p className="w-full text-[11px] text-slate-400">
                Contoh: {gen.awalan || 'Kamar'} {pad2(Math.max(1, Number(gen.mulai) || 1))} sampai{' '}
                {gen.awalan || 'Kamar'}{' '}
                {pad2(
                  Math.max(1, Number(gen.mulai) || 1) +
                    Math.max(1, Number(gen.jumlah) || 1) -
                    1
                )}
                . Nama yang sudah ada dilewati.
              </p>
            </div>
          </div>

          <div className="pt-4 flex items-center justify-end gap-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-xl"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saveMutation.isPending || loadingKamar}
              className="px-4 py-2 text-xs font-semibold text-white bg-emerald-700 hover:bg-emerald-800 disabled:bg-emerald-400 rounded-xl transition-colors cursor-pointer"
            >
              {saveMutation.isPending
                ? 'Menyimpan...'
                : editingItem
                ? 'Simpan Perubahan'
                : 'Tambah'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        title="Hapus Asrama"
        message="Asrama beserta seluruh kamarnya akan dihapus. Asrama yang kamarnya masih dihuni siswa tidak dapat dihapus, pindahkan siswanya dulu."
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}